import { useCallback, useState } from 'react';

import { VList } from 'virtua';

import type { FilesProxySchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import type { SpeakerLabels } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { getProxyTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';

import TranscriptionSegment from './transcription_segment/transcription_segment';
import TranscriptionSegmentEdit from './transcription_segment_edit/transcription_segment_edit';

type TranscriptionsSegmentsModeProps = {
  canEdit: boolean;
  canEditSpeakers: boolean;
  transcriptions?: SegmentEntityWithIdNormalized[];
  speakerLabels?: SpeakerLabels;
  assetProxy?: FilesProxySchemaWithId;
};

export function TranscriptionsSegmentsMode({
  canEdit,
  canEditSpeakers,
  transcriptions,
  speakerLabels,
  assetProxy,
}: TranscriptionsSegmentsModeProps) {
  const [editSegmentId, setEditSegmentId] = useState<string | undefined>();

  const editSegmentById = useCallback(
    (id: string | undefined) => {
      if (canEdit) {
        setEditSegmentId(id);
      }
    },
    [canEdit],
  );

  const timecodeOptions = assetProxy
    ? getProxyTimecodeOptions(assetProxy)
    : undefined;

  if (!transcriptions || !speakerLabels) {
    return null;
  }

  const transcriptionElements = transcriptions.map((transcription) => {
    if (!transcription) {
      return null;
    }
    return (
      <div key={transcription.id}>
        {transcription.id === editSegmentId ? (
          <TranscriptionSegmentEdit
            key={transcription.id}
            segment={transcription}
            canEditSpeakers={canEditSpeakers}
            timecodeOptions={timecodeOptions}
            onCancel={() => editSegmentById(undefined)}
            speakerLabels={speakerLabels}
          />
        ) : (
          <TranscriptionSegment
            key={transcription.id}
            segment={transcription}
            timecodeOptions={timecodeOptions}
            onEdit={() => editSegmentById(transcription.id)}
            speakerLabel={
              speakerLabels[
                transcription.transcription?.speaker ?? 'Speaker not set'
              ]
            }
          />
        )}
      </div>
    );
  });

  return <VList>{transcriptionElements}</VList>;
}
